import React, { useEffect, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import './OpenIdVcRequestQR.css';
import {
  createRequestId,
  createVerificationRequest,
  pollVpResult,
} from '../../utils';
import ErrorFallback from '../Error/ErrorFallback';
import { BannerContext } from '../../App';

const SHOW_RESULT_MS = 1500;
const PREVIEW_REQUEST_URI =
  'https://verifier.example/api/v1/verify/request-object?requestId=00000000-0000-4000-8000-000000000000';

const VERIFICATION_ERROR_MESSAGES = {
  request_object_failed: '검증 요청 정보를 확인하지 못했습니다.',
  holder_mismatch: '검증 요청과 제출된 DID가 일치하지 않습니다.',
  credential_missing: '티켓 증명서(VC)가 포함되어 있지 않습니다.',
  credential_schema_mismatch: '역할에 맞지 않는 티켓 형식이 제출되었습니다.',
  credential_ticket_number_mismatch:
    '검증 요청과 다른 티켓 번호가 제출되었습니다.',
  presentation_structure_invalid:
    '제출된 티켓 정보의 형식이 올바르지 않습니다.',
  presentation_signature_invalid: '제출 정보(VP)의 서명이 유효하지 않습니다.',
  credential_issuer_invalid: '티켓 발급자 DID 형식이 올바르지 않습니다.',
  credential_signature_invalid: '티켓 증명서(VC)의 서명이 유효하지 않습니다.',
  credential_status_missing: '대표구매자 티켓에 폐기 상태 정보가 없습니다.',
  credential_revoked: '폐기된 티켓입니다.',
  issuer_auth_failed: '티켓 발급자 DID 인증에 실패했습니다.',
  presentation_verification_failed: '티켓 검증 처리 중 오류가 발생했습니다.',
  holder_auth_failed: '티켓 제출자 DID 인증에 실패했습니다.',
  holder_challenge_failed: '티켓 제출자 인증 처리 중 오류가 발생했습니다.',
  verification_history_save_failed: '검증 결과를 저장하지 못했습니다.',
  verification_failed: '검증에 실패했습니다.',
  timeout: '검증 대기 시간이 초과되었습니다.',
  verification_status_unavailable: '검증 서버의 상태를 확인할 수 없습니다.',
  verification_request_id_missing:
    '검증 서버가 요청 식별자를 반환하지 않았습니다.',
  invalid_verification_status_response:
    '검증 서버의 응답 형식이 올바르지 않습니다.',
  verification_request_id_mismatch: '검증 요청 식별자가 일치하지 않습니다.',
  invalid_presenter_role: '검증된 티켓 역할을 확인할 수 없습니다.',
  secure_random_uuid_unavailable: '안전한 검증 요청 식별자를 만들 수 없습니다.',
};

const toVerificationErrorMessage = (reason) => {
  if (String(reason).startsWith('verification_request_creation_failed:')) {
    return '검증 요청을 생성하지 못했습니다.';
  }
  if (String(reason).startsWith('verification_status_http_404')) {
    return '검증 요청이 만료되었거나 존재하지 않습니다.';
  }
  if (String(reason).startsWith('verification_status_http_')) {
    return '검증 상태를 확인하지 못했습니다.';
  }
  return VERIFICATION_ERROR_MESSAGES[reason] || '검증에 실패했습니다.';
};

export default function OpenIdVcRequestQR({ previewPhase }) {
  const setBannerText = useContext(BannerContext);
  const navigate = useNavigate();
  const isPreview = Boolean(previewPhase);
  const [phase, setPhase] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [requestId, setRequestId] = useState(() =>
    isPreview ? '00000000-0000-4000-8000-000000000000' : createRequestId()
  );
  const [requestUri, setRequestUri] = useState(
    isPreview ? PREVIEW_REQUEST_URI : ''
  );

  const deepLink = useMemo(() => {
    if (!requestUri) return '';
    const base = 'uxmwallet://verify';
    const params = new URLSearchParams({
      request_uri: requestUri,
    });
    return `${base}?${params.toString()}`;
  }, [requestUri]);

  useEffect(() => {
    if (isPreview) {
      setPhase(previewPhase);
      setRequestUri(PREVIEW_REQUEST_URI);
      setErrorMsg('티켓 인증에 실패했습니다. QR 코드를 다시 발급해 주세요.');

      if (previewPhase === 'verified') {
        setBannerText('티켓 인증이 완료되었습니다.');
      } else if (previewPhase === 'error') {
        setBannerText('QR 인증에 실패했습니다.');
      } else if (previewPhase === 'processing') {
        setBannerText('Wallet에서 티켓을 인증하고 있습니다.');
      } else {
        setBannerText('Wallet 인증을 기다리고 있습니다.');
      }
      return undefined;
    }

    let active = true;
    let resultTimer = null;
    const controller = new AbortController();

    const startVerification = async () => {
      setPhase('idle');
      setErrorMsg('');
      setRequestUri('');
      setBannerText('검증 요청을 준비하고 있습니다.');

      try {
        const verificationRequest = await createVerificationRequest({
          requestId,
          signal: controller.signal,
        });
        if (!active) return;

        setRequestUri(verificationRequest.requestUri);
        setBannerText('화면의 QR 코드를 Wallet 앱으로 찍으세요!');

        const result = await pollVpResult({
          statusUrl: verificationRequest.statusUrl,
          requestId: verificationRequest.requestId,
          signal: controller.signal,
          onStatus: (status) => {
            if (
              active &&
              status.status === 'pending' &&
              status.phase &&
              status.phase !== 'awaiting_request_object'
            ) {
              setPhase('processing');
              setBannerText('Wallet에서 티켓을 인증하고 있습니다.');
            }
          },
        });
        if (!active || result?.aborted) return;

        if (result?.ok) {
          const presenterRole = String(result?.data?.role).toUpperCase();
          let role;
          if (presenterRole === 'COMPANION') role = '동반';
          else if (
            presenterRole === 'PRIMARY' ||
            presenterRole === 'REPRESENTATIVE'
          )
            role = '대표';
          else throw new Error('invalid_presenter_role');

          setPhase('verified');
          setBannerText(`${role} 티켓 인증 완료`);
          resultTimer = setTimeout(() => {
            if (!active) return;
            const params = new URLSearchParams({
              requestId: verificationRequest.requestId,
              role: presenterRole,
            });
            navigate(`/face-capture?${params.toString()}`, {
              replace: true,
              state: { role: presenterRole },
            });
          }, SHOW_RESULT_MS);
        }
      } catch (e) {
        if (!active || e?.name === 'AbortError' || e?.message === 'aborted')
          return;

        setErrorMsg(toVerificationErrorMessage(e?.message));
        setPhase('error');
        setBannerText('다시 시도하려면 아래 버튼을 누르세요.');
      }
    };

    startVerification();

    return () => {
      active = false;
      controller.abort();
      if (resultTimer) clearTimeout(resultTimer);
    };
  }, [isPreview, navigate, previewPhase, requestId, setBannerText]);

  return (
    <div className='qr-stage'>
      <div className='qr-box'>
        <div
          className={`did-auth-qr ${
            ['processing', 'verified', 'error'].includes(phase)
              ? 'is-blurred'
              : ''
          }`}
        >
          {deepLink && <QRCodeCanvas value={deepLink} size={260} />}
        </div>

        {phase === 'processing' && (
          <div className='qr-overlay fade-in' aria-live='polite'>
            <div className='icon-box'>
              <div className='qr-spinner' aria-label='티켓 인증 중' />
            </div>
          </div>
        )}

        {phase === 'verified' && (
          <div className='qr-overlay fade-in'>
            <div className='icon-box'>
              <div className='check-icon' aria-label='verified' />
            </div>
          </div>
        )}

        {phase === 'error' && (
          <ErrorFallback
            message={errorMsg}
            onRetry={() => {
              setPhase('idle');
              setErrorMsg('');
              setRequestUri('');
              setRequestId(createRequestId());
            }}
          />
        )}
      </div>
    </div>
  );
}

OpenIdVcRequestQR.propTypes = {
  previewPhase: PropTypes.oneOf([
    'idle',
    'processing',
    'verified',
    'error',
  ]),
};
